/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { Fragment, useEffect, useId, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { createRemixLoadingBarExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-loading-bar-experience';
import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';

type RemixThinkingBarOptionKind =
	| 'chart'
	| 'diagram'
	| 'infographic'
	| 'visual-aid'
	| 'slides'
	| 'whiteboard'
	| 'database'
	| 'custom'
	| 'image';

const messages = defineMessages({
	rovoIsThinking: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.rovo-is-thinking',
		defaultMessage: 'Rovo is thinking',
		description: 'Initial loading message shown while Rovo is starting to generate a remix',
	},
	analyzingContent: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.analyzing-content',
		defaultMessage: 'Analyzing your content',
		description: 'Loading message shown while Rovo is analyzing the content for a remix',
	},
	puttingPiecesTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.putting-pieces-together',
		defaultMessage: 'Putting the pieces together',
		description: 'Loading message shown while Rovo is assembling content for a remix',
	},
	structuringRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.structuring-remix',
		defaultMessage: 'Structuring your remix',
		description: 'Loading message shown while Rovo is structuring the remix output',
	},
	designingRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.designing-remix',
		defaultMessage: 'Designing your remix',
		description: 'Loading message shown while Rovo is designing the remix output',
	},
	structuringYourContent: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.structuring-content',
		defaultMessage: 'Structuring your content',
		description: 'Loading message shown while Rovo is structuring the created content',
	},
	designingYourContent: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.designing-content',
		defaultMessage: 'Designing your content',
		description: 'Loading message shown while Rovo is designing the created content',
	},
	addingItAllTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.adding-it-all-together',
		defaultMessage: 'Adding it all together',
		description: 'Loading message shown while Rovo is combining all elements of the remix',
	},
	applyingFinalTouches: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.applying-final-touches',
		defaultMessage: 'Applying the final touches',
		description: 'Loading message shown while Rovo is finalizing the remix',
	},
	almostDone: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished generating a remix',
	},
});

const rotatingMessagesNonRemixEntrance = defineMessages({
	rovoIsImagining: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.rovo-is-imagining',
		defaultMessage: 'Rovo is dreaming it up',
		description: 'Initial loading message shown while Rovo is starting to generate an image.',
	},
	sketchingSomeIdeas: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.sketching-some-ideas',
		defaultMessage: 'Sketching ideas',
		description: 'Loading message shown while Rovo is beginning to form the image.',
	},
	mixingTheColors: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.mixing-the-colors',
		defaultMessage: 'Mixing colors',
		description: 'Loading message shown while Rovo is adding color to the image.',
	},
	findingTheRightStyle: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.finding-the-right-style',
		defaultMessage: 'Finding the right style',
		description: 'Loading message shown while Rovo is refining the visual style.',
	},
	bringingItToLife: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.bringing-it-to-life',
		defaultMessage: 'Bringing it to life',
		description: 'Loading message shown while Rovo is rendering the image.',
	},
	addingTheDetails: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.adding-the-details',
		defaultMessage: 'Adding details',
		description: 'Loading message shown while Rovo is adding finer details.',
	},
	applyingTheFinalTouches: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.applying-the-final-touches',
		defaultMessage: 'Applying final touches',
		description: 'Loading message shown while Rovo is finalizing the image.',
	},
	almostDone: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.image-almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished generating the image.',
	},
});

const styles = cssMap({
	// Positioning only — RovoThinkingBarWrapper supplies the visual chrome.
	rovoWrapperFixed: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
	},
	// As `rovoWrapperFixed`, but centered over the content area rather than the viewport.
	// The horizontal placement (`left` + `transform`) is set imperatively via inline styles
	// from a measured content-container rect — see the `parentDetector` below — because a
	// `position: fixed` element is otherwise positioned relative to the viewport and would
	// stay window-centered when a sidenav/sidebar opens.
	rovoWrapperFixedContentCentered: {
		position: 'fixed',
		bottom: token('space.200'),
		width: 'fit-content',
		// Paired with the measured `left` (set inline) to center the bar on its anchor point.
		transform: 'translateX(-50%)',
		zIndex: 600,
	},
	// Zero-impact marker rendered in the content flow; its parent element is measured to
	// derive the content-area bounds. Sanctioned alternative to reading page-layout CSS vars.
	parentDetector: {
		position: 'absolute',
		visibility: 'hidden',
		pointerEvents: 'none',
	},
	rovoWrapperAbsolute: {
		position: 'absolute',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
	},
	rovoWrapperRelative: {
		position: 'relative',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
	},
});

type RemixThinkingBarProps = {
	onStop: () => void;
	remixAttemptId?: string;
	loadingBarType?: 'big-bar' | 'mini-bar';
	optionType?: RemixThinkingBarOptionKind;
	/** Render the bar with relative positioning instead of fixed/absolute overlay positioning. */
	isRelative?: boolean;
	/** Render a loading progress bar (and swap the animated border for a solid one). */
	showProgressBar?: boolean;
	/** When `showProgressBar`, rapidly fill to 100% then fire `onCompleteAnimationFinished`. */
	isComplete?: boolean;
	/** Fires once the progress bar finishes animating to 100%. */
	onCompleteAnimationFinished?: () => void;
	/**
	 * The content type of the document being edited (e.g. `'jira.issue'`, `'confluence.page'`).
	 * When the content type indicates an inline editor context (e.g. a Jira issue comment),
	 * the bar is positioned absolutely within the editor wrapper rather than fixed to the viewport.
	 */
	contentType?: string;
	/**
	 * When true, uses the remix rotating titles.
	 * Remix entrance is the initial loading state when the user clicks the remix button.
	 * Non-remix entrance is using BE but not from remix
	 */
	isRemixEntrance?: boolean;
	/**
	 * When true (and on the fixed/overlay path), measure the surrounding content container and
	 * center the fixed bar over it instead of the viewport. Intended for stable view surfaces
	 * (e.g. the classic view page). Leave off in the editor, whose reflows make the one-shot
	 * measurement unreliable and cause the bar to drift.
	 */
	shouldMeasureContentBounds?: boolean;
};

/**
 * A floating thinking bar that displays a Rovo loading animation, a sequentially
 * rotating status text, an Esc keyboard hint, and a stop button. Matches the
 * visual style of the inline chat ThinkingView / AI loading screen.
 *
 * The status text progresses through a fixed ordered sequence:
 * "Rovo is thinking" → "Analyzing your content" → "Putting the pieces together"
 * → "Structuring your remix" → "Designing your remix" → "Adding it all together"
 * → "Applying the final touches" → "Almost done"
 */
export function RemixThinkingBar({
	onStop,
	remixAttemptId,
	optionType: _optionType,
	isRelative = false,
	loadingBarType = isRelative ? 'mini-bar' : 'big-bar',
	showProgressBar = false,
	isComplete = false,
	onCompleteAnimationFinished,
	contentType,
	isRemixEntrance = true,
	shouldMeasureContentBounds = false,
}: RemixThinkingBarProps): JSX.Element {
	const { sendAnalyticsEvent } = useAnalytics();
	const loadingBarExperienceInstanceId = useId();
	const loadingBarMetadataRef = useRef({
		remixAttemptId,
		loadingBarType,
		contentType,
		optionType: _optionType,
		isRemixEntrance,
		hasCompletionHandshake: onCompleteAnimationFinished !== undefined,
	});

	useEffect(() => {
		const loadingBarExperience = createRemixLoadingBarExperience(loadingBarExperienceInstanceId);
		loadingBarExperience.start();
		loadingBarExperience.addMetadata(loadingBarMetadataRef.current);
		if (loadingBarMetadataRef.current.isRemixEntrance) {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'remixProgressIndicator',
				attributes: {
					remixType: loadingBarMetadataRef.current.optionType,
				},
			});
		}

		return () => {
			loadingBarExperience.success();
		};
	}, [loadingBarExperienceInstanceId, sendAnalyticsEvent]);

	const positionAbsolute = contentType === 'jira.issue';
	// Confluence page remix uses the fixed/overlay path (no `contentType`, not `isRelative`).
	// Center the bar over the content area instead of the viewport so it stays aligned when a
	// sidenav/sidebar is open. Jira (`positionAbsolute`) is unaffected.
	const shouldCenterOnContent = shouldMeasureContentBounds;
	// Measure the content container (the detector marker's parent) so the fixed bar can be
	// centered on the content rather than the viewport. Re-measures on container resize, which
	// covers a sidenav/sidebar opening or closing. Stays viewport-centered until measured.
	const parentDetectorRef = useRef<HTMLDivElement>(null);
	const [contentCenterX, setContentCenterX] = useState<number | null>(null);
	useEffect(() => {
		if (!shouldCenterOnContent) {
			return;
		}
		const parentContainer = parentDetectorRef.current?.parentElement;
		if (!parentContainer) {
			return;
		}
		const updateCenter = () => {
			const rect = parentContainer.getBoundingClientRect();
			setContentCenterX(rect.left + rect.width / 2);
		};
		const resizeObserver = new ResizeObserver(updateCenter);
		resizeObserver.observe(parentContainer);
		updateCenter();
		return () => resizeObserver.disconnect();
	}, [shouldCenterOnContent]);
	const { formatMessage } = useIntl();
	const thinkingTexts = [
		formatMessage(messages.rovoIsThinking),
		formatMessage(messages.analyzingContent),
		formatMessage(messages.puttingPiecesTogether),
		formatMessage(
			expValEquals('cc_maui_create_keyword', 'isEnabled', true)
				? messages.structuringYourContent
				: messages.structuringRemix,
		),
		formatMessage(
			expValEquals('cc_maui_create_keyword', 'isEnabled', true)
				? messages.designingYourContent
				: messages.designingRemix,
		),
		formatMessage(messages.addingItAllTogether),
		formatMessage(messages.applyingFinalTouches),
		formatMessage(messages.almostDone),
	];

	const thinkingTextsNonRemixEntrance = [
		formatMessage(rotatingMessagesNonRemixEntrance.rovoIsImagining),
		formatMessage(rotatingMessagesNonRemixEntrance.sketchingSomeIdeas),
		formatMessage(rotatingMessagesNonRemixEntrance.mixingTheColors),
		formatMessage(rotatingMessagesNonRemixEntrance.findingTheRightStyle),
		formatMessage(rotatingMessagesNonRemixEntrance.bringingItToLife),
		formatMessage(rotatingMessagesNonRemixEntrance.addingTheDetails),
		formatMessage(rotatingMessagesNonRemixEntrance.applyingTheFinalTouches),
		formatMessage(rotatingMessagesNonRemixEntrance.almostDone),
	];

	const barContent = (
		<RovoThinkingBarWrapper isThinking>
			<RovoThinkingBar
				onStop={onStop}
				showProgressBar={showProgressBar}
				isComplete={isComplete}
				onCompleteAnimationFinished={onCompleteAnimationFinished}
				useRotatingTitle
				customRotatingMessages={isRemixEntrance ? thinkingTexts : thinkingTextsNonRemixEntrance}
			/>
		</RovoThinkingBarWrapper>
	);

	// Content-centered fixed path (Confluence view page, gated + opted in via
	// `shouldMeasureContentBounds`). Renders a hidden marker so the content container can be
	// measured, then centers the fixed bar on the content's horizontal midpoint. Falls back to
	// viewport-centering (`left: 50%`) until the measurement resolves.
	if (!isRelative && !positionAbsolute && shouldCenterOnContent) {
		return (
			<>
				<Box
					ref={parentDetectorRef}
					xcss={styles.parentDetector}
					testId="remix-thinking-bar-parent-detector"
				/>
				<Box
					xcss={styles.rovoWrapperFixedContentCentered}
					style={{
						minWidth: fg('platform_maui_jira_ui_fix') ? 'min(400px, 100%)' : '400px',
						left: contentCenterX != null ? `${contentCenterX}px` : '50%',
					}}
					testId="remix-thinking-bar-gradient-border"
				>
					{barContent}
				</Box>
			</>
		);
	}

	return (
		<Box
			xcss={
				// eslint-disable-next-line no-nested-ternary
				isRelative
					? styles.rovoWrapperRelative
					: positionAbsolute
						? styles.rovoWrapperAbsolute
						: styles.rovoWrapperFixed
			}
			style={{
				minWidth: fg('platform_maui_jira_ui_fix') ? 'min(400px, 100%)' : '400px',
			}}
			testId="remix-thinking-bar-gradient-border"
		>
			{barContent}
		</Box>
	);
}
